/**
 * Purchase orders API — the ordering and receiving workflow that replaced incoming
 * shipments.
 *
 * A PO is generated from the PO template, the client renders it to a PDF and posts
 * that back as the first attachment, and the PO then sits in the Receiving table
 * until every line is accounted for.
 *
 * Stored status is Open / Partially Received / Fully Received / Cancelled.
 * `display_status` adds Overdue, which the server derives from
 * `expected_delivery_date` rather than storing, so a PO goes overdue on its own and
 * stops being overdue as soon as the date is corrected. Render `display_status` and
 * branch on `status`.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, isVoided, patchAttachmentVoid } from './attachmentVoid';
import { generatePurchaseOrderPdf, purchaseOrderPdfFileName } from '../utils/purchaseOrderPdf';
import type { InventoryUnitCondition, InventoryUnitStatus } from '../data/inventory';

export type PurchaseOrderStatus = 'Open' | 'Partially Received' | 'Fully Received' | 'Cancelled';
export type PurchaseOrderDisplayStatus = PurchaseOrderStatus | 'Overdue';
export type PurchaseOrderReceivingStatus = 'Complete' | 'Partial' | 'Rejected - Return Needed';
export type PurchaseOrderCondition = 'Good' | 'Damaged' | 'Partial Damage' | 'Wrong Item';
export type PurchaseOrderInspection = 'Pass' | 'Fail' | 'Pending';
export type PurchaseOrderApproval = 'Pending' | 'Approved' | 'Rejected';

export const PURCHASE_ORDER_CONDITIONS: PurchaseOrderCondition[] = [
  'Good',
  'Damaged',
  'Partial Damage',
  'Wrong Item',
];

export const PURCHASE_ORDER_INSPECTIONS: PurchaseOrderInspection[] = ['Pass', 'Fail', 'Pending'];

export interface PurchaseOrderRow {
  id: number;
  po_number: string;
  status: PurchaseOrderStatus;
  /** What to show the user: `status`, or 'Overdue' when past the expected delivery date. */
  display_status: PurchaseOrderDisplayStatus;
  is_overdue: boolean;
  /** The receiving log's own status, null until the PO has been received against. */
  receiving_status: PurchaseOrderReceivingStatus | null;

  date_issued: string | null;
  requested_by: string | null;
  approval_status: PurchaseOrderApproval;
  approved_by: string | null;
  approved_date: string | null;

  vendor_name: string;
  vendor_address: string | null;
  vendor_contact_person: string | null;
  vendor_phone_email: string | null;
  payment_terms: string | null;

  ship_to_name: string | null;
  ship_to_address: string | null;
  ship_to_attn: string | null;
  ship_to_phone_email: string | null;
  requested_delivery: string | null;
  expected_delivery_date: string | null;
  /** The vendor's or carrier's latest arrival estimate. Overdue still follows expected_delivery_date. */
  eta: string | null;

  subtotal: number;
  shipping: number;
  processing_fee: number;
  tax: number;
  /** Subtotal + shipping + processing fee + tax. */
  total: number;
  /** What the order was expected to cost when it was placed; not part of the total. */
  initial_estimated_cost: number | null;
  notes_terms: string | null;

  date_received: string | null;
  /** The tracking number: set on the PO form before delivery, or when a receipt is recorded. */
  carrier_tracking: string | null;
  condition_on_arrival: PurchaseOrderCondition | null;
  inspection_result: PurchaseOrderInspection | null;
  discrepancy_notes: string | null;
  received_by_user_id: number | null;
  received_by: string | null;

  cancel_reason: string | null;
  cancelled_at: string | null;
  fully_received_at: string | null;

  item_count: number;
  qty_ordered: number;
  qty_received: number;
  /** Received minus ordered: negative is a shortage, positive an overage. */
  qty_variance: number;
  first_description: string | null;
  custom_count: number;
  /** Received lines that have not been put into inventory yet. */
  awaiting_inventory_count: number;
  attachment_count: number;
  note_count: number;

  created_by_user_id: number | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Goods are only received or stocked against an approved order; the server refuses the rest. */
export function canReceivePurchaseOrder(po: Pick<PurchaseOrderRow, 'approval_status'>): boolean {
  return po.approval_status === 'Approved';
}

/** An approved order that has taken goods keeps its approval; the server refuses to withdraw it. */
export function purchaseOrderApprovalLocked(
  po: Pick<PurchaseOrderRow, 'approval_status' | 'status' | 'qty_received'>
): boolean {
  return (
    po.approval_status === 'Approved' &&
    (po.qty_received > 0 || po.status === 'Partially Received' || po.status === 'Fully Received')
  );
}

export interface PurchaseOrderItemRow {
  id: number;
  purchase_order_id: number;
  line_no: number;
  /** Set when the line was picked from the catalog; null for a write-in. */
  product_id: string | null;
  product_name: string | null;
  product_type: string | null;
  is_custom: boolean;
  description: string;
  qty: number;
  unit_price: number;
  line_total: number;
  qty_received: number;
  qty_outstanding: number;
  /** How many of qty_received have already been written into inventory. */
  qty_added_to_inventory: number;
  added_to_inventory_at: string | null;
  /** The product a write-in was promoted to, if the user created one. */
  created_product_id: string | null;
}

export interface PurchaseOrderNoteRow {
  id: number;
  purchase_order_id: number;
  user_id: number | null;
  note: string;
  submitted_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Waybills get their own section on the PO; everything else is a general attachment. */
export type PurchaseOrderAttachmentKind = 'attachment' | 'waybill';

export interface PurchaseOrderAttachmentRow extends AttachmentVoidFields {
  id: number;
  purchase_order_id: number;
  original_file_name: string;
  mime_type: string | null;
  file_size: number | null;
  /** True for the PDF written when the PO was generated. */
  is_generated_pdf: boolean;
  kind: PurchaseOrderAttachmentKind;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

/** The two dashboard cards, computed from the same rows the tables render. */
export interface PurchaseOrderSummary {
  ordering: {
    total: number;
    open: number;
    partiallyReceived: number;
    fullyReceived: number;
    cancelled: number;
    overdue: number;
    totalSpend: number;
  };
  receiving: {
    totalReceived: number;
    complete: number;
    partial: number;
    rejected: number;
    qtyVariance: number;
    failedInspections: number;
    pendingInspections: number;
    awaitingInventory: number;
  };
}

export interface PurchaseOrderListResponse {
  /** Still owed to us: Open and partially received, so also every Overdue row. */
  receiving: PurchaseOrderRow[];
  /** Closed out — fully received. */
  orderLog: PurchaseOrderRow[];
  cancelled: PurchaseOrderRow[];
  summary: PurchaseOrderSummary;
}

export interface PurchaseOrderDetail {
  purchaseOrder: PurchaseOrderRow;
  items: PurchaseOrderItemRow[];
  notes: PurchaseOrderNoteRow[];
  attachments: PurchaseOrderAttachmentRow[];
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchPurchaseOrders(): Promise<PurchaseOrderListResponse> {
  const res = await fetch(`${API_BASE}/api/purchase-orders`);
  return jsonOrThrow(res, 'Failed to load purchase orders');
}

export async function fetchPurchaseOrder(id: number): Promise<PurchaseOrderDetail> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}`);
  return jsonOrThrow(res, 'Failed to load purchase order');
}

export interface PurchaseOrderItemInput {
  /** Omitted for a write-in. */
  product_id?: string | null;
  is_custom: boolean;
  description: string;
  qty: number;
  unit_price: number;
}

export interface CreatePurchaseOrderPayload {
  user_id?: number | null;
  /** Omit to have the server assign the next PO-<year>-<seq>; set it to write one in. */
  po_number?: string | null;
  date_issued?: string | null;
  requested_by?: string | null;
  approval_status?: PurchaseOrderApproval;
  approved_by?: string | null;
  approved_date?: string | null;

  vendor_name: string;
  vendor_address?: string | null;
  vendor_contact_person?: string | null;
  vendor_phone_email?: string | null;
  payment_terms?: string | null;

  ship_to_name?: string | null;
  ship_to_address?: string | null;
  ship_to_attn?: string | null;
  ship_to_phone_email?: string | null;
  requested_delivery?: string | null;

  /** Required — drives the Overdue status. */
  expected_delivery_date: string;
  eta?: string | null;
  carrier_tracking?: string | null;

  shipping?: number;
  processing_fee?: number;
  tax?: number;
  initial_estimated_cost?: number | null;
  notes_terms?: string | null;
  items: PurchaseOrderItemInput[];
}

/** The purchase order already holding a number a user tried to write in. */
export interface PurchaseOrderNumberMatch {
  id: number;
  po_number: string;
  status?: PurchaseOrderStatus;
  vendor_name?: string;
}

/**
 * Thrown when a written-in PO number is taken, carrying the purchase order that has
 * it so the form can link straight to it.
 */
export class PurchaseOrderNumberConflictError extends Error {
  readonly conflict: PurchaseOrderNumberMatch | null;

  constructor(message: string, conflict: PurchaseOrderNumberMatch | null) {
    super(message);
    this.name = 'PurchaseOrderNumberConflictError';
    this.conflict = conflict;
  }
}

/**
 * Whether a PO number is free, for checking a write-in before the form is submitted.
 * Matching ignores case, the same as the create endpoint.
 */
export async function lookupPurchaseOrderNumber(poNumber: string): Promise<PurchaseOrderNumberMatch | null> {
  const res = await fetch(
    `${API_BASE}/api/purchase-orders/lookup/by-number?po_number=${encodeURIComponent(poNumber)}`
  );
  const data = await jsonOrThrow<{ match: PurchaseOrderNumberMatch | null }>(res, 'Failed to check that PO number');
  return data.match;
}

export async function createPurchaseOrder(
  payload: CreatePurchaseOrderPayload
): Promise<{ purchaseOrder: PurchaseOrderRow; items: PurchaseOrderItemRow[] }> {
  const res = await fetch(`${API_BASE}/api/purchase-orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) {
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      conflict?: PurchaseOrderNumberMatch | null;
    };
    throw new PurchaseOrderNumberConflictError(
      data.error || 'That PO number is already in use.',
      data.conflict ?? null
    );
  }
  return jsonOrThrow(res, 'Failed to generate purchase order');
}

export interface UpdatePurchaseOrderPayload {
  status?: PurchaseOrderStatus;
  cancel_reason?: string | null;
  /** Renumbering the PO; rejected with a conflict if another order has it. */
  po_number?: string;
  /**
   * Replaces every line wholesale. Only accepted while nothing has been received —
   * the server rejects it otherwise rather than strand a received quantity.
   */
  items?: PurchaseOrderItemInput[];
  date_issued?: string | null;
  requested_by?: string | null;
  approval_status?: PurchaseOrderApproval;
  approved_by?: string | null;
  approved_date?: string | null;
  vendor_name?: string;
  vendor_address?: string | null;
  vendor_contact_person?: string | null;
  vendor_phone_email?: string | null;
  payment_terms?: string | null;
  ship_to_name?: string | null;
  ship_to_address?: string | null;
  ship_to_attn?: string | null;
  ship_to_phone_email?: string | null;
  requested_delivery?: string | null;
  expected_delivery_date?: string | null;
  eta?: string | null;
  shipping?: number;
  processing_fee?: number;
  tax?: number;
  initial_estimated_cost?: number | null;
  notes_terms?: string | null;
  carrier_tracking?: string | null;
  condition_on_arrival?: PurchaseOrderCondition | null;
  inspection_result?: PurchaseOrderInspection | null;
  discrepancy_notes?: string | null;
  date_received?: string | null;
}

export async function updatePurchaseOrder(
  id: number,
  payload: UpdatePurchaseOrderPayload
): Promise<PurchaseOrderRow> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) {
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      conflict?: PurchaseOrderNumberMatch | null;
    };
    throw new PurchaseOrderNumberConflictError(
      data.error || 'That PO number is already in use.',
      data.conflict ?? null
    );
  }
  const data = await jsonOrThrow<{ purchaseOrder: PurchaseOrderRow }>(res, 'Failed to update purchase order');
  return data.purchaseOrder;
}

export function cancelPurchaseOrder(id: number, reason: string): Promise<PurchaseOrderRow> {
  return updatePurchaseOrder(id, { status: 'Cancelled', cancel_reason: reason });
}

/** Puts a cancelled PO back in the receiving queue. */
export function reopenPurchaseOrder(id: number): Promise<PurchaseOrderRow> {
  return updatePurchaseOrder(id, { status: 'Open' });
}

// ── Receiving ──

export interface ReceivePurchaseOrderPayload {
  user_id?: number | null;
  receipt_type: 'Full' | 'Partial';
  date_received?: string | null;
  carrier_tracking?: string | null;
  condition_on_arrival?: PurchaseOrderCondition | null;
  inspection_result?: PurchaseOrderInspection | null;
  discrepancy_notes?: string | null;
  /**
   * Partial receipts only. `qty_received` is the cumulative total for the line, not
   * the delta, so topping up a partial means sending the new running total.
   */
  items?: Array<{ id: number; qty_received: number }>;
}

export async function receivePurchaseOrder(
  id: number,
  payload: ReceivePurchaseOrderPayload
): Promise<{ purchaseOrder: PurchaseOrderRow; items: PurchaseOrderItemRow[] }> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/receive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to record the receipt');
}

export interface StockUnitInput {
  /** The full asset tag serial, as the Inventory page stores it. */
  serial_number?: string;
  model?: string;
  country_of_origin?: string;
  /** Asset tag category, e.g. GARG. */
  product_type?: string;
  manufacturer?: string | null;
  location?: string | null;
  business?: string | null;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition | null;
  assembled?: boolean;
  /** For a non-serialized row that carries a count. */
  stock_quantity?: number;
}

export interface StockLineInput {
  item_id: number;
  /** Target catalog product. Required unless `create_product` is supplied. */
  product_id?: string | null;
  /** Promotes a write-in to a real catalog product before stocking it. */
  create_product?: {
    name: string;
    type: 'Robot' | 'Accessory' | 'Stock Item';
    sku?: string;
    replacement_cost_per_item?: string | null;
  };
  /** One entry per serialized unit. */
  units?: StockUnitInput[];
  /**
   * Used instead of `units` for goods that are counted rather than serialized: one
   * inventory row carrying this count and the fields below. Robots are refused this way.
   */
  stock_quantity?: number;
  manufacturer?: string | null;
  location?: string | null;
  business?: string | null;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition | null;
  assembled?: boolean;
}

export interface AddToInventoryResponse {
  purchaseOrder: PurchaseOrderRow;
  items: PurchaseOrderItemRow[];
  unitsCreated: number;
  productsCreated: Array<{ itemId: number; productId: string; name: string }>;
}

/**
 * Puts received lines into inventory. Lines the user declined to stock are simply
 * left out, so they stay flagged as awaiting inventory rather than being stocked
 * behind the user's back.
 */
export async function addPurchaseOrderToInventory(
  id: number,
  lines: StockLineInput[],
  userId?: number | null
): Promise<AddToInventoryResponse> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/add-to-inventory`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lines, actorUserId: userId ?? null }),
  });
  return jsonOrThrow(res, 'Failed to add the items to inventory');
}

// ── Notes ──

export async function fetchPurchaseOrderNotes(id: number): Promise<PurchaseOrderNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/notes`);
  const data = await jsonOrThrow<{ notes: PurchaseOrderNoteRow[] }>(res, 'Failed to load notes');
  return data.notes || [];
}

export async function addPurchaseOrderNote(
  id: number,
  note: string,
  userId?: number | null
): Promise<PurchaseOrderNoteRow> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId ?? null }),
  });
  return jsonOrThrow(res, 'Failed to add note');
}

// ── Attachments ──

export async function fetchPurchaseOrderAttachments(id: number): Promise<PurchaseOrderAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/attachments`);
  const data = await jsonOrThrow<{ attachments: PurchaseOrderAttachmentRow[] }>(res, 'Failed to load attachments');
  return data.attachments || [];
}

export async function uploadPurchaseOrderAttachment(
  id: number,
  file: File | Blob,
  opts: {
    fileName?: string;
    userId?: number | null;
    isGeneratedPdf?: boolean;
    kind?: PurchaseOrderAttachmentKind;
  } = {}
): Promise<PurchaseOrderAttachmentRow> {
  const form = new FormData();
  form.append('file', file, opts.fileName ?? (file instanceof File ? file.name : 'upload'));
  if (opts.userId != null && opts.userId > 0) form.append('user_id', String(opts.userId));
  if (opts.isGeneratedPdf) form.append('is_generated_pdf', '1');
  if (opts.kind) form.append('kind', opts.kind);
  const res = await fetch(`${API_BASE}/api/purchase-orders/${id}/attachments`, { method: 'POST', body: form });
  return jsonOrThrow(res, 'Failed to upload attachment');
}

export function purchaseOrderAttachmentUrl(id: number, attachmentId: number): string {
  return `${API_BASE}/api/purchase-orders/${id}/attachments/${attachmentId}/download`;
}

/** Sets a file aside with a reason. It stays on the PO and can be restored. */
export function voidPurchaseOrderAttachment(
  id: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<PurchaseOrderAttachmentRow> {
  return patchAttachmentVoid(`${API_BASE}/api/purchase-orders/${id}/attachments/${attachmentId}`, reason, userId);
}

export function unvoidPurchaseOrderAttachment(
  id: number,
  attachmentId: number,
  userId?: number | null
): Promise<PurchaseOrderAttachmentRow> {
  return patchAttachmentVoid(`${API_BASE}/api/purchase-orders/${id}/attachments/${attachmentId}`, null, userId);
}

/**
 * Renders the PO to a PDF and attaches it as the generated document, superseding the
 * previous one.
 *
 * The old PDF is voided rather than deleted, so the paperwork that was sent to a
 * vendor stays on the record and readable, while `is_generated_pdf && !voided` still
 * identifies exactly one current document. Upload happens first: if voiding then
 * fails, the PO has a current PDF and a stale one rather than none at all.
 */
export async function attachGeneratedPurchaseOrderPdf(
  po: PurchaseOrderRow,
  items: PurchaseOrderItemRow[],
  userId?: number | null
): Promise<PurchaseOrderAttachmentRow> {
  const superseded = (await fetchPurchaseOrderAttachments(po.id)).filter(
    (a) => a.is_generated_pdf && !isVoided(a)
  );

  const pdf = await generatePurchaseOrderPdf({
    poNumber: po.po_number,
    dateIssued: po.date_issued ?? '',
    requestedBy: po.requested_by ?? '',
    status: po.display_status,
    vendorName: po.vendor_name,
    vendorAddress: po.vendor_address ?? '',
    vendorContactPerson: po.vendor_contact_person ?? '',
    vendorPhoneEmail: po.vendor_phone_email ?? '',
    paymentTerms: po.payment_terms ?? '',
    shipToName: po.ship_to_name ?? '',
    shipToAddress: po.ship_to_address ?? '',
    shipToAttn: po.ship_to_attn ?? '',
    shipToPhoneEmail: po.ship_to_phone_email ?? '',
    requestedDelivery: po.requested_delivery ?? '',
    expectedDelivery: po.expected_delivery_date ?? '',
    items: items.map((i) => ({ description: i.description, qty: i.qty, unitPrice: i.unit_price })),
    shipping: po.shipping,
    processingFee: po.processing_fee,
    tax: po.tax,
    notesTerms: po.notes_terms ?? '',
    approvedBy: po.approved_by ?? '',
    approvedDate: po.approved_date ?? '',
  });

  const attachment = await uploadPurchaseOrderAttachment(po.id, pdf, {
    fileName: purchaseOrderPdfFileName(po.po_number),
    userId,
    isGeneratedPdf: true,
  });

  for (const old of superseded) {
    await voidPurchaseOrderAttachment(po.id, old.id, 'Superseded by a regenerated PO PDF', userId).catch(() => {
      /* the current PDF is attached; a failed tidy-up must not fail the whole action */
    });
  }

  return attachment;
}

/** Matches a PO against the search box across the fields shown in the tables. */
export function purchaseOrderMatchesQuery(po: PurchaseOrderRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    po.po_number,
    po.vendor_name,
    po.first_description,
    po.display_status,
    po.receiving_status,
    po.requested_by,
    po.created_by,
    po.received_by,
    po.carrier_tracking,
    po.expected_delivery_date,
    po.eta,
    po.date_received,
  ]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(q));
}
